// ── suggest.js — подсказки перевода для новой карточки (как в Quizlet) ────────
// Цепочка источников, от бесплатного к точному:
//   1) общий словарь (Supabase shared_dict) — мгновенно, бесплатно, уже проверенные статьи;
//   2) Claude, если у пользователя есть свой ключ — точный перевод + чтение; результат
//      сразу кладётся в общий словарь, чтобы следующему человеку достался бесплатно;
//   3) сервер сайта /api/translate (бесплатная модель OpenRouter, кэш на CDN);
//   4) MyMemory — бесплатный машинный перевод, если ничего лучше нет.
// Для японского дополнительно даём кану (из словаря/Claude или из kuromoji).

import { getSharedDict, addSharedDict } from './library';

const cache = new Map(); // `${lang}|${xl}|${word}` → результат

export const CARD_XL_KEY = 'ac_card_xl';
export const getCardXl = () => { try { return localStorage.getItem(CARD_XL_KEY) || 'ru'; } catch { return 'ru'; } };

const keyOf = (w) => String(w || '').trim().toLowerCase();
const split = (t) => [...new Set(String(t || '').split(/\s*[,;]\s*/).map((s) => s.trim()).filter(Boolean))].slice(0, 5);
const hasKanji = (w) => /[一-龯㐀-䶿]/.test(w);
export const toHiragana = (s) => String(s || '').replace(/[ァ-ヶ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60));

async function kanaFor(word) {
  try {
    const { ensureJaTokenizer } = await import('./jaTokenizer');
    const tk = await ensureJaTokenizer();
    const toks = tk.tokenize(word);
    if (!toks.length || toks.some((t) => !t.reading && hasKanji(t.surface_form))) return '';
    return toHiragana(toks.map((t) => t.reading || t.surface_form).join(''));
  } catch { return ''; }
}

async function myMemory(word, lang, xl, signal) {
  try {
    const r = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(word)}&langpair=${lang}|${xl}`, { signal });
    const j = await r.json();
    const all = [j?.responseData?.translatedText, ...(j?.matches || []).filter((m) => (m.quality | 0) >= 50 || m.quality === undefined).map((m) => m.translation)];
    return [...new Set(all.map((s) => String(s || '').trim()).filter((s) => s && s.length < 60 && keyOf(s) !== keyOf(word)))].slice(0, 4);
  } catch { return []; }
}

/**
 * onUpdate({ options, reading, source, loading }) вызывается по мере появления данных:
 * сначала быстрый результат, потом уточнённый.
 */
export async function suggestTranslation({ word, lang, xl, signal, onUpdate }) {
  const w = String(word || '').trim();
  const k = `${lang}|${xl}|${keyOf(w)}`;
  if (cache.has(k)) { onUpdate(cache.get(k)); return; }
  let res = { options: [], reading: '', source: '', loading: true };
  const push = (patch) => { res = { ...res, ...patch }; if (!signal?.aborted) onUpdate(res); };
  const needKana = lang === 'ja' && hasKanji(w);

  try {
    // Без связи с базой (офлайн, сессия обновляется) не ждём дольше 2,5 с — идём к следующему источнику.
    const m = await Promise.race([getSharedDict(lang, xl, [w]), new Promise((r) => setTimeout(() => r(new Map()), 2500))]);
    const t = m.get(keyOf(w));
    if (t?.translation) push({ options: split(t.translation), reading: t.reading || '', source: 'dict' });
  } catch {}
  if (signal?.aborted) return;

  let claudeKey = '';
  try { claudeKey = (localStorage.getItem('ac_claude_key') || '').trim(); } catch {}
  const { isCustomAIReady } = await import('./customAI');
  if (!res.options.length && (claudeKey || isCustomAIReady())) {
    try {
      const { lookupWords } = await import('./claude');
      const { map } = await lookupWords({ apiKey: claudeKey, lang, xl, items: [{ word: w, sentence: '' }], signal });
      const t = map.get(keyOf(w));
      if (t?.translation) {
        push({ options: split(t.translation), reading: t.reading || res.reading, source: 'claude' });
        addSharedDict(lang, xl, new Map([[w, t]])).catch(() => {});
      }
    } catch {}
    if (signal?.aborted) return;
  }

  // Сервер сайта (бесплатная модель через OpenRouter, ответы кэшируются CDN) —
  // заметно точнее MyMemory. Удачный ответ тоже уходит в общий словарь.
  if (!res.options.length) {
    try {
      const base = window.location.hostname === 'audiocards.vercel.app' ? '' : 'https://audiocards.vercel.app';
      const r = await fetch(`${base}/api/translate?w=${encodeURIComponent(w)}&l=${lang}&x=${xl}`, { signal });
      const j = r.ok ? await r.json() : null;
      const opts = (j?.translations || []).filter((s) => typeof s === 'string' && s.trim());
      if (opts.length) {
        // Чтение японских слов бесплатные модели путают (祈り → «くずり») — его берём из kuromoji ниже.
        const srvReading = lang === 'ja' ? '' : (j.reading || '').trim();
        push({ options: opts.slice(0, 4), reading: srvReading || res.reading, source: 'server' });
        addSharedDict(lang, xl, new Map([[w, { translation: opts.join(', '), reading: srvReading }]])).catch(() => {});
      }
    } catch {}
    if (signal?.aborted) return;
  }

  if (!res.options.length) {
    const opts = await myMemory(w, lang, xl, signal);
    if (signal?.aborted) return;
    if (opts.length) push({ options: opts, source: 'mymemory' });
  }

  if (needKana && !res.reading) {
    const r = await kanaFor(w); // только если ни словарь, ни Claude не дали чтения (kuromoji, словарный разбор)
    if (signal?.aborted) return;
    if (r && r !== w) push({ reading: r });
  }
  push({ loading: false });
  if (res.options.length || res.reading) cache.set(k, res);
}
